// src/components/ProductCard.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';

const StyledCard = styled(Card)(({ theme }) => ({
  width: '100%',
  height: '100%',
  borderRadius: 24,
  position: 'relative',
  overflow: 'hidden',
  background: '#fff',
  userSelect: 'none',
  WebkitUserSelect: 'none',
  MozUserSelect: 'none',
  msUserSelect: 'none',
  // border: '1px solid rgba(0, 0, 0, 0.12)',
  display: 'flex',
  flexDirection: 'column',
}));

const ProductImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  userDrag: 'none',
  WebkitUserDrag: 'none',
  transition: 'transform 0.3s ease',
});

const ImageContainer = styled(Box)({
  position: 'relative',
  height: '100%',
  overflow: 'hidden',
  '&:hover img': {
    transform: 'scale(1.05)',
  },
  flexGrow: 1,
  minHeight: 0,
});

const ContentOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  background: 'linear-gradient(to top, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 40%, rgba(255,255,255,0) 100%)',
  padding: theme.spacing(3, 2, 4, 2),
  paddingTop: theme.spacing(6),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  maxHeight: '40%',
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '100%',
    background: 'linear-gradient(to top, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 100%)',
    pointerEvents: 'none',
  }
}));

const ChipsContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(2),
  display: 'flex',
  gap: theme.spacing(1),
  zIndex: 2,
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(8px)',
  border: '1px solid rgba(0, 0, 0, 0.12)',
  borderRadius: 24,
  transition: 'transform 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    borderColor: 'rgba(0, 0, 0, 0.2)',
  }
}));

const InfoButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(8px)',
  border: '1px solid rgba(0, 0, 0, 0.12)',
  borderRadius: '50%',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
    transform: 'translateY(-2px)',
    borderColor: 'rgba(0, 0, 0, 0.2)',
  }
}));

const InfoOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(255,255,255,0.98)',
  backdropFilter: 'blur(10px)',
  padding: theme.spacing(3),
  borderRadius: 24,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  zIndex: 2,
  animation: 'slideIn 0.3s ease',
  '@keyframes slideIn': {
    from: {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  }
}));

const StyleScore = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  '& .MuiTypography-root': {
    minWidth: 90,
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  '& .MuiLinearProgress-root': {
    flexGrow: 1,
    height: 8,
    borderRadius: 12,
  },
}));

export default function ProductCard({ 
  id,
  image,
  brand,
  name,
  price,
  originalPrice,
  discountPercentage,
  productDetails,
  variants,
  category,
  color,
  styleScores,
  product
}) {
  const [showInfo, setShowInfo] = React.useState(false);
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/product/${id}`, { state: { product } });
  };

  const formatPrice = (price) => {
    if (!price) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const getTopStyles = () => {
    if (!styleScores) return [];
    return Object.entries(styleScores)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3)
      .filter(([,score]) => score > 0);
  };

  // Get original price from product details if not directly provided
  const effectiveOriginalPrice = originalPrice || productDetails?.original_price;

  return (
    <StyledCard>
      <ImageContainer>
        <ProductImage 
          src={image} 
          alt={name} 
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/400x600?text=Image+Not+Available';
          }}
        />
        
        <ChipsContainer>
          {discountPercentage > 0 && (
            <StyledChip
              label={`-${discountPercentage}%`}
              size="small"
              color="error"
            />
          )}
        </ChipsContainer>

        <ContentOverlay>
          <Typography 
            variant="subtitle1" 
            component="h2"
            sx={{ 
              fontWeight: 600,
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: 'text.primary',
            }}
          >
            {brand}
          </Typography>

          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography 
              variant="h6" 
              color="primary"
              sx={{ 
                fontWeight: 700,
                lineHeight: 1
              }}
            >
              {formatPrice(price)}
              {effectiveOriginalPrice && effectiveOriginalPrice > price && (
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ 
                    textDecoration: 'line-through',
                    marginLeft: 1
                  }}
                >
                  {formatPrice(effectiveOriginalPrice)}
                </Typography>
              )}
            </Typography>
          </Box>
        </ContentOverlay>
      </ImageContainer>
    </StyledCard>
  );
}